import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";
import { useTheme } from "../ThemeProvider";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import { geocodeDireccionPropiedad, reverseGeocodePropiedad } from "../../services/geocodePropiedad";
import {
  buildDireccionPropiedad,
  formatCoordenadas,
  mapsUrlFromCoords,
  parseCoordsInput,
} from "../../utils/buildDireccionPropiedad";
import PropiedadMapaUbicacion from "./PropiedadMapaUbicacion";
import {
  normalizeProvinciaId,
  normalizePoblacionId,
  provinciaIdsEqual,
  poblacionIdsEqual,
} from "../../utils/normalizeLocalizacionIds";
import {
  resolverProvinciaId,
  matchPoblacionPlacesEnLista,
  normLocalidadNombre,
} from "../../utils/resolverLocalidadDesdePlaces";
import {
  POBLACIONES_FAVORITAS_ALICANTE,
  CP_POR_POBLACION,
} from "../../utils/poblacionesFavoritas";
import { parseTipoViaCalle } from "../../utils/parseTipoViaCalle";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";

const PROVINCIAS_FAVORITAS = [
  { id: "03", nombre: "Alicante" },
  { id: "46", nombre: "Valencia" },
];

const TODAS_PROVINCIAS = [
  { id: "01", nombre: "Álava" },{ id: "02", nombre: "Albacete" },{ id: "03", nombre: "Alicante" },
  { id: "04", nombre: "Almería" },{ id: "05", nombre: "Ávila" },{ id: "06", nombre: "Badajoz" },
  { id: "07", nombre: "Baleares" },{ id: "08", nombre: "Barcelona" },{ id: "09", nombre: "Burgos" },
  { id: "10", nombre: "Cáceres" },{ id: "11", nombre: "Cádiz" },{ id: "12", nombre: "Castellón" },
  { id: "13", nombre: "Ciudad Real" },{ id: "14", nombre: "Córdoba" },{ id: "15", nombre: "A Coruña" },
  { id: "16", nombre: "Cuenca" },{ id: "17", nombre: "Girona" },{ id: "18", nombre: "Granada" },
  { id: "19", nombre: "Guadalajara" },{ id: "20", nombre: "Guipúzcoa" },{ id: "21", nombre: "Huelva" },
  { id: "22", nombre: "Huesca" },{ id: "23", nombre: "Jaén" },{ id: "24", nombre: "León" },
  { id: "25", nombre: "Lleida" },{ id: "26", nombre: "La Rioja" },{ id: "27", nombre: "Lugo" },
  { id: "28", nombre: "Madrid" },{ id: "29", nombre: "Málaga" },{ id: "30", nombre: "Murcia" },
  { id: "31", nombre: "Navarra" },{ id: "32", nombre: "Ourense" },{ id: "33", nombre: "Asturias" },
  { id: "34", nombre: "Palencia" },{ id: "35", nombre: "Las Palmas" },{ id: "36", nombre: "Pontevedra" },
  { id: "37", nombre: "Salamanca" },{ id: "38", nombre: "S.C. Tenerife" },{ id: "39", nombre: "Cantabria" },
  { id: "40", nombre: "Segovia" },{ id: "41", nombre: "Sevilla" },{ id: "42", nombre: "Soria" },
  { id: "43", nombre: "Tarragona" },{ id: "44", nombre: "Teruel" },{ id: "45", nombre: "Toledo" },
  { id: "46", nombre: "Valencia" },{ id: "47", nombre: "Valladolid" },{ id: "48", nombre: "Vizcaya" },
  { id: "49", nombre: "Zamora" },{ id: "50", nombre: "Zaragoza" },{ id: "51", nombre: "Ceuta" },
  { id: "52", nombre: "Melilla" },
];

/** Distancia aproximada en metros (para detectar pin GPS desalineado de la dirección). */
function distanciaMetros(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

function esCodigoPostalManual(form) {
  return form?.codigo_postal_manual === true || form?.codigo_postal_manual === "true";
}

export default function SeccionLocalizacion(props) {
  const { form, cambiar, cambiarDirecto, auxiliares } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerZona = puede("zona", "ver");
  const puedeModificarZona = puede("zona", "modificar");
  const puedeVerCodigoPostal = puede("codigo_postal", "ver");
  const puedeModificarCodigoPostal = puede("codigo_postal", "modificar");
  const puedeVerNombreVia = puede("nombre_via", "ver");
  const puedeModificarNombreVia = puede("nombre_via", "modificar");
  const puedeVerNumeroPiso = puede("numero_piso", "ver");
  const puedeModificarNumeroPiso = puede("numero_piso", "modificar");
  const puedeVerGps = puede("gps", "ver");
  const puedeModificarGps = puede("gps", "modificar");
  const puedeVerLocUsarUbicacion = puede("loc_usar_ubicacion", "ver");
  const puedeVerLocMapa = puede("loc_mapa", "ver");
  const puedeModificarLocMapa = puede("loc_mapa", "modificar");

  const zonas = auxiliares["zona"] || [];
  const urbanizaciones = auxiliares["urbanizacion"] || [];
  const tiposVia = auxiliares["tipo-via"] || [];

  const [poblaciones, setPoblaciones] = useState([]);
  const [cargandoPob, setCargandoPob] = useState(false);
  const [mostrarMasProvincias, setMostrarMasProvincias] = useState(false);
  const [mostrarMasPoblaciones, setMostrarMasPoblaciones] = useState(false);
  const [placesMunicipioContext, setPlacesMunicipioContext] = useState(null);
  const [geocodificando, setGeocodificando] = useState(false);
  const [reverseGeocificando, setReverseGeocificando] = useState(false);
  const [obteniendoGps, setObteniendoGps] = useState(false);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false,
  );
  const [geocodeError, setGeocodeError] = useState("");
  const [coordsTexto, setCoordsTexto] = useState("");
  const direccionRef = useRef(null);
  const ultimaDireccionGeocod = useRef("");
  const geocodeGenRef = useRef(0);
  const reverseGenRef = useRef(0);
  const omitirForwardGeocodeRef = useRef(false);
  const coordsEditandoRef = useRef(false);
  const reverseMapDebounceRef = useRef(null);
  const reverseTargetRef = useRef(null);
  const reconcilePinRef = useRef(false);
  const cpManualRef = useRef(esCodigoPostalManual(form));
  cpManualRef.current = esCodigoPostalManual(form);

  /** CP automático (población / Places / reverse) solo si el usuario no lo fijó a mano. */
  const setCodigoPostalAuto = useCallback(
    (cp) => {
      const v = cp != null ? String(cp).trim() : "";
      if (!v) return;
      if (cpManualRef.current) return;
      cambiarDirecto("codigo_postal", v);
    },
    [cambiarDirecto],
  );

  const onCodigoPostalChange = (e) => {
    const v = e.target.value;
    cambiarDirecto("codigo_postal", v);
    cambiarDirecto("codigo_postal_manual", true);
  };

  // Chip estilo FichaCliente
  const chipStyle = (activo) => ({
    padding: "4px 10px", fontSize: 11, fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: "pointer", transition: "all 0.15s ease",
    userSelect: "none", lineHeight: "1.4",
  });
  const onEnter = (e, activo) => {
    if (!activo) { e.currentTarget.style.background = c.chipHoverBg; e.currentTarget.style.color = c.chipHoverText; e.currentTarget.style.borderColor = c.chipHoverBorder; }
  };
  const onLeave = (e, activo) => {
    if (!activo) { e.currentTarget.style.background = c.chipBg; e.currentTarget.style.color = c.chipText; e.currentTarget.style.borderColor = c.chipBorder; }
  };

  const seleccionarProvincia = async (provId) => {
    if (provId === form.provincia_id) return;
    cambiarDirecto("provincia_id", provId);
    cambiarDirecto("poblacion_id", "");
    cambiarDirecto("poblacion_nombre", "");
    cambiarDirecto("poblacion", "");
    cambiarDirecto("codigo_postal", "");
    cambiarDirecto("codigo_postal_manual", false);
    setMostrarMasProvincias(false);
    setMostrarMasPoblaciones(false);
    if (!provId) { setPoblaciones([]); return; }
    setCargandoPob(true);
    try {
      const res = await api.get("/auxiliares/poblaciones/" + provId);
      setPoblaciones(res.data.data || []);
    } catch { setPoblaciones([]); }
    setCargandoPob(false);
  };

  const seleccionarPoblacion = (pobId, pobNombre = "") => {
    cambiarDirecto("poblacion_id", pobId || "");
    cambiarDirecto("poblacion_nombre", pobNombre || "");
    cambiarDirecto("poblacion", "");
    if (pobId && CP_POR_POBLACION[pobId]) setCodigoPostalAuto(CP_POR_POBLACION[pobId]);
    setMostrarMasPoblaciones(false);
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 8, display: "block",
  };
  const inputStyle = {
    padding: "8px 12px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 6, fontSize: 13, color: c.textPrimary,
    background: isDark ? '#0a0e16' : '#dceeff', width: "100%", outline: "none",
    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)',
  };
  const selectStyle = { ...inputStyle, cursor: "pointer" };

  // Panel flotante para "más opciones"
  const panelStyle = {
    background: c.bgCard,
    border: `1px solid ${c.border}`,
    borderRadius: 10,
    padding: 12,
    position: "relative",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    display: "flex", flexWrap: "wrap", gap: 5,
    maxWidth: 520,
    maxHeight: 220,
    overflowY: "auto",
  };

  const provNorm = normalizeProvinciaId(form.provincia_id);
  const pobNorm = normalizePoblacionId(form.poblacion_id);
  const provEsFavorita = PROVINCIAS_FAVORITAS.some((p) => provinciaIdsEqual(p.id, form.provincia_id));
  const provActual = TODAS_PROVINCIAS.find((p) => provinciaIdsEqual(p.id, form.provincia_id));
  const pobFav = provNorm === "03" ? POBLACIONES_FAVORITAS_ALICANTE : [];
  const pobFavIds = new Set(pobFav.map((p) => normalizePoblacionId(p.id)));
  // «Más» debe listar TODAS (incl. favoritas). Antes se excluían y Beniarbeig
  // (chip) no salía al buscarla entre Benejúzar y Beniardà.
  const pobListaMas = [...poblaciones].sort((a, b) =>
    String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" }),
  );
  const pobActualFav = pobFav.find((p) => poblacionIdsEqual(p.id, form.poblacion_id));
  const pobActualResto = poblaciones.find((p) => poblacionIdsEqual(p.id, form.poblacion_id));
  const pobActual = pobActualFav || pobActualResto;
  const tipoViaNombre = tiposVia.find((t) => String(t.id) === String(form.tipo_via_id))?.nombre || "";

  /** Aplica calle/número y resuelve tipo de vía desde sugerencia Google o reverse geocode. */
  const aplicarCalleDesdePlaces = useCallback(
    (parts) => {
      // Preferir vía de Google; si viene corta, ampliar con el 1.er tramo de la sugerencia.
      let rawLine = String(parts?.calle || "").trim();
      const suggest = String(parts?.suggestion_text || parts?.direccion || "").trim();
      if (suggest) {
        const first = suggest.split(",")[0].trim();
        const firstSinNum = first
          .replace(/[,\s]+(?:n[ºo°.]?\s*)?(\d{1,5}[a-zA-Z]?(?:\s*[-/]\s*\d{1,5}[a-zA-Z]?)?)\s*$/i, "")
          .trim();
        if (firstSinNum && (!rawLine || firstSinNum.length > rawLine.length)) {
          rawLine = firstSinNum;
        }
      }
      const parsed = parseTipoViaCalle(rawLine, tiposVia);
      const numero = parts?.numero || parsed.numero || "";
      if (parsed.tipoViaId) cambiarDirecto("tipo_via_id", parsed.tipoViaId);
      // Dirección completa: conservar la vía de Google (p. ej. "Avinguda de Paris"), no solo "Paris".
      let calleCompleta = rawLine;
      if (numero && calleCompleta) {
        const esc = String(numero).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        calleCompleta = calleCompleta
          .replace(new RegExp(`[,\\s]*(?:n[ºo°.]?\\s*)?${esc}\\s*$`, "i"), "")
          .trim();
      }
      if (calleCompleta) cambiarDirecto("nombre_via", calleCompleta);
      else if (parsed.nombreVia) cambiarDirecto("nombre_via", parsed.nombreVia);
      if (numero) cambiarDirecto("numero", String(numero));
      return { ...parsed, numero: String(numero), nombreVia: calleCompleta || parsed.nombreVia };
    },
    [tiposVia, cambiarDirecto],
  );

  useEffect(() => {
    if (!pobActual?.nombre || !form.poblacion_id) return;
    const custom = String(form.poblacion_nombre || "").trim();
    if (custom && normLocalidadNombre(custom) !== normLocalidadNombre(pobActual.nombre)) {
      return;
    }
    if (form.poblacion_nombre !== pobActual.nombre) {
      cambiarDirecto("poblacion_nombre", pobActual.nombre);
      cambiarDirecto("poblacion", "");
    }
  }, [form.poblacion_id, pobActual?.nombre, form.poblacion_nombre, cambiarDirecto]);

  const nombrePoblacionGeocode =
    (form.poblacion_nombre && String(form.poblacion_nombre).trim())
    || pobActual?.nombre
    || "";

  const ctxGeocode = {
    tipoViaNombre,
    poblacionNombre: nombrePoblacionGeocode,
    provinciaNombre: provActual?.nombre,
  };

  const tieneCoordsValidas = useCallback(() => {
    const la = Number(form.latitud);
    const ln = Number(form.longitud);
    return Number.isFinite(la) && Number.isFinite(ln);
  }, [form.latitud, form.longitud]);

  const ejecutarGeocode = useCallback(async () => {
    if (omitirForwardGeocodeRef.current) return;
    const address = buildDireccionPropiedad(form, ctxGeocode);
    if (!form.nombre_via?.trim() || address.replace(/España/gi, "").trim().length < 8) {
      setGeocodeError("");
      return;
    }
    if (address === ultimaDireccionGeocod.current && tieneCoordsValidas()) {
      setGeocodeError("");
      return;
    }
    const gen = ++geocodeGenRef.current;
    setGeocodificando(true);
    setGeocodeError("");
    try {
      const data = await geocodeDireccionPropiedad(form, ctxGeocode);
      if (gen !== geocodeGenRef.current) return;
      if (data?.ok && data.latitud != null && data.longitud != null) {
        cambiarDirecto("latitud", data.latitud);
        cambiarDirecto("longitud", data.longitud);
        setCoordsTexto(formatCoordenadas(data.latitud, data.longitud));
        ultimaDireccionGeocod.current = address;
        setGeocodeError("");
      } else if (!tieneCoordsValidas()) {
        setGeocodeError("No se encontraron coordenadas para esta dirección");
      }
    } catch {
      if (gen !== geocodeGenRef.current) return;
      if (!tieneCoordsValidas()) setGeocodeError("Error al obtener coordenadas");
    } finally {
      if (gen === geocodeGenRef.current) setGeocodificando(false);
    }
  }, [form, ctxGeocode, cambiarDirecto, tieneCoordsValidas]);

  const pobGeocodeSkipRef = useRef(true);
  useEffect(() => {
    pobGeocodeSkipRef.current = false;
  }, []);

  useEffect(() => {
    if (pobGeocodeSkipRef.current || !form.nombre_via?.trim()) return;
    const t = setTimeout(() => void ejecutarGeocode(), 450);
    return () => clearTimeout(t);
  }, [form.poblacion_id, form.poblacion_nombre, form.nombre_via, ejecutarGeocode]);

  /**
   * Una vez al abrir: si el pin guardado (p. ej. GPS) está lejos de la dirección escrita,
   * reubicar el pin. Ampliar: no toca dirección ni CP.
   */
  useEffect(() => {
    if (reconcilePinRef.current) return;
    if (!form.nombre_via?.trim() || !nombrePoblacionGeocode) return;
    if (omitirForwardGeocodeRef.current) return;
    reconcilePinRef.current = true;
    const address = buildDireccionPropiedad(form, ctxGeocode);
    if (address.replace(/España/gi, "").trim().length < 8) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await geocodeDireccionPropiedad(form, ctxGeocode);
        if (cancelled || !data?.ok || data.latitud == null || data.longitud == null) return;
        const la = Number(form.latitud);
        const ln = Number(form.longitud);
        if (Number.isFinite(la) && Number.isFinite(ln)) {
          const dist = distanciaMetros(la, ln, Number(data.latitud), Number(data.longitud));
          if (dist < 250) {
            ultimaDireccionGeocod.current = address;
            return;
          }
        }
        omitirForwardGeocodeRef.current = true;
        cambiarDirecto("latitud", data.latitud);
        cambiarDirecto("longitud", data.longitud);
        setCoordsTexto(formatCoordenadas(data.latitud, data.longitud));
        ultimaDireccionGeocod.current = address;
        setGeocodeError("");
        setTimeout(() => {
          omitirForwardGeocodeRef.current = false;
        }, 600);
      } catch {
        /* sin pin: el geocode normal puede reintentar */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo al hidratar dirección/población; no en cada tecleo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.nombre_via, form.numero, form.poblacion_id, nombrePoblacionGeocode]);

  const onDireccionBlur = () => {
    setTimeout(() => {
      if (direccionRef.current && !direccionRef.current.contains(document.activeElement)) {
        void ejecutarGeocode();
      }
    }, 120);
  };

  const aplicarLocalidadDesdePlaces = useCallback(
    async (parts) => {
      let working = parts || {};
      const tieneAlgo =
        working.provincia
        || working.poblacion
        || working.suggestion_text
        || working.direccion
        || (Array.isArray(working.poblacion_candidatos) && working.poblacion_candidatos.length)
        || (working.latitud != null && working.longitud != null);
      if (!tieneAlgo) return;

      const cargarLista = async (provId) => {
        const id = normalizeProvinciaId(provId);
        if (!id) return [];
        try {
          const res = await api.get("/auxiliares/poblaciones/" + id);
          return res.data.data || [];
        } catch {
          return [];
        }
      };

      let provNombre = working.provincia?.trim() || "";
      let provId = provNombre ? resolverProvinciaId(provNombre, TODAS_PROVINCIAS) : "";
      if (!provId) provId = provNorm;
      if (provId && !provinciaIdsEqual(provId, form.provincia_id)) {
        cambiarDirecto("provincia_id", provId);
      }

      let provEfectiva = normalizeProvinciaId(provId || form.provincia_id);
      if (!provEfectiva) return;

      setCargandoPob(true);
      let listaPob = await cargarLista(provEfectiva);
      setPoblaciones(listaPob);
      setCargandoPob(false);

      const listaDe = (lista) => [
        ...(provEfectiva === "03" ? POBLACIONES_FAVORITAS_ALICANTE : []),
        ...lista,
      ];

      let { id: pobId, nombre: pobNombre } = matchPoblacionPlacesEnLista(
        working,
        listaDe(listaPob),
      );

      // Genérico: si Google solo dio barrio/playa, reverse-geocode por pin → municipio real.
      // Vale para cualquier pueblo; no hace falta alias por barrio.
      if (!pobId && working.latitud != null && working.longitud != null) {
        try {
          const rev = await reverseGeocodePropiedad(working.latitud, working.longitud);
          if (rev?.ok && (rev.poblacion || rev.direccion || rev.provincia || rev.poblacion_candidatos?.length)) {
            working = {
              ...working,
              poblacion: rev.poblacion || working.poblacion,
              provincia: rev.provincia || working.provincia,
              codigo_postal: working.codigo_postal || rev.codigo_postal || "",
              direccion: rev.direccion || working.direccion,
              poblacion_candidatos: [
                ...(Array.isArray(rev.poblacion_candidatos) ? rev.poblacion_candidatos : []),
                rev.poblacion,
                ...(Array.isArray(working.poblacion_candidatos) ? working.poblacion_candidatos : []),
              ].filter(Boolean),
            };
            if (rev.provincia) {
              const provRev = resolverProvinciaId(rev.provincia, TODAS_PROVINCIAS);
              if (provRev && !provinciaIdsEqual(provRev, provEfectiva)) {
                cambiarDirecto("provincia_id", provRev);
                provEfectiva = normalizeProvinciaId(provRev);
                setCargandoPob(true);
                listaPob = await cargarLista(provEfectiva);
                setPoblaciones(listaPob);
                setCargandoPob(false);
              }
            }
            const m2 = matchPoblacionPlacesEnLista(working, listaDe(listaPob));
            if (m2.id) {
              pobId = m2.id;
              pobNombre = m2.nombre;
            } else if (m2.nombre) {
              pobNombre = m2.nombre;
            }
          }
        } catch {
          /* sin reverse: se queda el intento Places */
        }
      }

      if (pobId) {
        cambiarDirecto("poblacion_id", pobId);
        cambiarDirecto("poblacion_nombre", pobNombre);
        cambiarDirecto("poblacion", "");
        if (working.codigo_postal?.trim()) setCodigoPostalAuto(working.codigo_postal.trim());
        else if (CP_POR_POBLACION[pobId]) setCodigoPostalAuto(CP_POR_POBLACION[pobId]);
      } else if (pobNombre) {
        // Ampliar: no vaciar población ya elegida si Google trae solo un barrio sin match.
        cambiarDirecto("poblacion_nombre", pobNombre);
        cambiarDirecto("poblacion", "");
        if (working.codigo_postal?.trim()) setCodigoPostalAuto(working.codigo_postal.trim());
      }
    },
    [form.provincia_id, provNorm, cambiarDirecto, setCodigoPostalAuto],
  );

  const aplicarCoordsDesdePlaces = (parts) => {
    if (parts?.latitud != null && parts?.longitud != null) {
      // Places ya dio el pin bueno: no dejar que un geocode posterior (con piso/puerta) lo pise.
      omitirForwardGeocodeRef.current = true;
      cambiarDirecto("latitud", parts.latitud);
      cambiarDirecto("longitud", parts.longitud);
      setCoordsTexto(formatCoordenadas(parts.latitud, parts.longitud));
      ultimaDireccionGeocod.current = buildDireccionPropiedad(
        {
          ...form,
          nombre_via: parts.calle || form.nombre_via,
          numero: parts.numero || form.numero,
          codigo_postal: parts.codigo_postal || form.codigo_postal,
        },
        {
          tipoViaNombre,
          poblacionNombre: parts.poblacion || pobActual?.nombre,
          provinciaNombre: parts.provincia || provActual?.nombre,
        },
      );
      setGeocodeError("");
      setTimeout(() => {
        omitirForwardGeocodeRef.current = false;
      }, 800);
    }
  };

  const aplicarDesdeReverseGeocode = useCallback(
    async (lat, lng) => {
      const la = Number(lat);
      const ln = Number(lng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return;

      if (reverseMapDebounceRef.current) {
        clearTimeout(reverseMapDebounceRef.current);
        reverseMapDebounceRef.current = null;
      }
      const gen = ++reverseGenRef.current;
      setReverseGeocificando(true);
      setGeocodeError("");
      try {
        cambiarDirecto("latitud", la);
        cambiarDirecto("longitud", ln);
        setCoordsTexto(formatCoordenadas(la, ln));

        const data = await reverseGeocodePropiedad(la, ln);
        if (gen !== reverseGenRef.current) return;

        if (!data?.ok) {
          if (data?.error === "coords_invalid" || data?.error === "coords_required") {
            setGeocodeError("Coordenadas no válidas");
          }
          return;
        }

        setGeocodeError("");
        omitirForwardGeocodeRef.current = true;

        if (!data.tiene_direccion) {
          setGeocodeError("Coordenadas guardadas; no se pudo resolver la calle. Complétala a mano.");
          return;
        }

        if (data.calle) aplicarCalleDesdePlaces(data);
        else cambiarDirecto("numero", data.numero != null && data.numero !== "" ? String(data.numero) : "");
        if (data.codigo_postal) setCodigoPostalAuto(data.codigo_postal);
        await aplicarLocalidadDesdePlaces(data);
        ultimaDireccionGeocod.current = buildDireccionPropiedad(
          {
            ...form,
            nombre_via: data.calle || form.nombre_via,
            numero: data.numero || form.numero,
            codigo_postal: data.codigo_postal || form.codigo_postal,
          },
          {
            tipoViaNombre,
            poblacionNombre: data.poblacion || pobActual?.nombre,
            provinciaNombre: data.provincia || provActual?.nombre,
          },
        );
      } catch {
        if (gen === reverseGenRef.current) {
          setGeocodeError("Error al obtener la dirección desde coordenadas");
        }
      } finally {
        if (gen === reverseGenRef.current) setReverseGeocificando(false);
        setTimeout(() => {
          omitirForwardGeocodeRef.current = false;
        }, 500);
      }
    },
    [form, tipoViaNombre, pobActual?.nombre, provActual?.nombre, cambiarDirecto, aplicarLocalidadDesdePlaces, aplicarCalleDesdePlaces, setCodigoPostalAuto],
  );

  const programarReverseGeocode = useCallback(
    (lat, lng) => {
      const la = Number(lat);
      const ln = Number(lng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
      reverseTargetRef.current = { lat: la, lng: ln };
      if (reverseMapDebounceRef.current) clearTimeout(reverseMapDebounceRef.current);
      reverseMapDebounceRef.current = setTimeout(() => {
        reverseMapDebounceRef.current = null;
        const t = reverseTargetRef.current;
        if (!t) return;
        void aplicarDesdeReverseGeocode(t.lat, t.lng);
      }, 500);
    },
    [aplicarDesdeReverseGeocode],
  );

  const mapsUrl = mapsUrlFromCoords(form.latitud, form.longitud);
  const textoCoords = formatCoordenadas(form.latitud, form.longitud);
  const coordsVerde = Boolean(textoCoords || parseCoordsInput(coordsTexto));
  const colorCoords = c.accentGreen || "#22c55e";

  useEffect(() => {
    if (coordsEditandoRef.current) return;
    const t = formatCoordenadas(form.latitud, form.longitud);
    if (t) setCoordsTexto(t);
    else if (!form.latitud && !form.longitud) setCoordsTexto("");
  }, [form.latitud, form.longitud]);

  useEffect(
    () => () => {
      if (reverseMapDebounceRef.current) clearTimeout(reverseMapDebounceRef.current);
    },
    [],
  );

  const onCoordsManualBlur = () => {
    coordsEditandoRef.current = false;
    const parsed = parseCoordsInput(coordsTexto);
    if (!coordsTexto.trim()) return;
    if (!parsed) {
      setGeocodeError("Formato: latitud, longitud (ej. 38.840840, 0.105690)");
      return;
    }
    const actual = formatCoordenadas(form.latitud, form.longitud);
    const nuevo = formatCoordenadas(parsed.lat, parsed.lng);
    if (actual === nuevo) return;
    void aplicarDesdeReverseGeocode(parsed.lat, parsed.lng);
  };

  const onMapCoordsChange = useCallback(
    (lat, lng) => {
      cambiarDirecto("latitud", lat);
      cambiarDirecto("longitud", lng);
      setCoordsTexto(formatCoordenadas(lat, lng));
      programarReverseGeocode(lat, lng);
    },
    [cambiarDirecto, programarReverseGeocode],
  );

  const usarUbicacionActual = () => {
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setGeocodeError("La ubicación requiere HTTPS (o localhost)");
      return;
    }
    if (!navigator.geolocation) {
      setGeocodeError("Tu navegador no soporta geolocalización");
      return;
    }
    setObteniendoGps(true);
    setGeocodeError("");

    const aplicarPos = (pos) => {
      setObteniendoGps(false);
      void aplicarDesdeReverseGeocode(pos.coords.latitude, pos.coords.longitude);
    };
    const onErr = (err, esFallback) => {
      if (!esFallback) {
        // Ampliar: reintentar sin alta precisión (Wi‑Fi / red).
        navigator.geolocation.getCurrentPosition(
          aplicarPos,
          (err2) => onErr(err2, true),
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 },
        );
        return;
      }
      setObteniendoGps(false);
      if (err?.code === 1) setGeocodeError("Permiso de ubicación denegado. Actívalo en el navegador.");
      else if (err?.code === 2) setGeocodeError("No se pudo obtener la ubicación");
      else if (err?.code === 3) setGeocodeError("Tiempo de espera agotado. Activa el GPS o sal al exterior.");
      else setGeocodeError("Error al obtener ubicación actual");
    };

    navigator.geolocation.getCurrentPosition(
      aplicarPos,
      (err) => onErr(err, false),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 15000 },
    );
  };

  const mapaActivo =
    Boolean(form.nombre_via?.trim()) ||
    (form.latitud != null && form.latitud !== "" && form.longitud != null && form.longitud !== "");

  const mapaFallbackCenter = placesMunicipioContext
    ? { lat: placesMunicipioContext.latitude, lng: placesMunicipioContext.longitude }
    : null;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsNarrow(e.matches);
    onChange(mq);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const gpsBusy = obteniendoGps || reverseGeocificando;
  const btnGpsStyle = {
    padding: isNarrow ? "12px 16px" : "7px 12px",
    minHeight: isNarrow ? 44 : 36,
    borderRadius: isNarrow ? 10 : 6,
    border: `1px solid ${c.accentBlue || "#2563eb"}`,
    background: isDark ? "rgba(37,99,235,0.18)" : "#eff6ff",
    color: c.accentBlue || "#2563eb",
    fontSize: isNarrow ? 13 : 11,
    fontWeight: 700,
    cursor: gpsBusy ? "wait" : "pointer",
    whiteSpace: "nowrap",
    opacity: gpsBusy ? 0.65 : 1,
    fontFamily: "inherit",
    touchAction: "manipulation",
    width: isNarrow ? "100%" : undefined,
  };

  useEffect(() => {
    if (!provNorm) {
      setPoblaciones([]);
      return undefined;
    }
    let cancelled = false;
    setCargandoPob(true);
    (async () => {
      try {
        const res = await api.get("/auxiliares/poblaciones/" + provNorm);
        if (!cancelled) setPoblaciones(res.data.data || []);
      } catch {
        if (!cancelled) setPoblaciones([]);
      }
      if (!cancelled) setCargandoPob(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [provNorm]);

  useEffect(() => {
    let alive = true;
    const pobNombre = (
      (form.poblacion_nombre && String(form.poblacion_nombre).trim())
      || pobActual?.nombre?.trim()
      || ""
    );
    const provNombre = provActual?.nombre?.trim();
    if (!pobNombre) {
      setPlacesMunicipioContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pobNombre, provincia: provNombre || "", pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesMunicipioContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesMunicipioContext(null);
      } catch {
        if (alive) setPlacesMunicipioContext(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [form.poblacion_id, form.poblacion_nombre, form.provincia_id, pobActual?.nombre, provActual?.nombre]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── PROVINCIA ──────────────────────────────────────────────────────── */}
      <div>
        <label style={labelStyle}>{tv("Provincia")}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>

          {PROVINCIAS_FAVORITAS.map(p => {
            const activo = provinciaIdsEqual(form.provincia_id, p.id);
            return (
              <button key={p.id} type="button"
                onClick={() => seleccionarProvincia(p.id)}
                style={chipStyle(activo)}
                onMouseEnter={e => onEnter(e, activo)}
                onMouseLeave={e => onLeave(e, activo)}
              >
                {p.nombre}
              </button>
            );
          })}

          {/* Si hay una provincia activa que no es favorita, mostrarla */}
          {provNorm && !provEsFavorita && (
            <button type="button" style={chipStyle(true)}>
              {provActual?.nombre || provNorm}
            </button>
          )}

          {/* Botón + Más */}
          {!mostrarMasProvincias ? (
            <button type="button"
              onClick={() => setMostrarMasProvincias(true)}
              style={{ ...chipStyle(false), color: c.accentBlue, fontWeight: 700 }}>
              + {tv("Más")}
            </button>
          ) : (
            <div style={{ ...panelStyle, paddingTop: 28 }}>
              <button type="button" onClick={() => setMostrarMasProvincias(false)}
                style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: c.textMuted, fontSize: 16, cursor: "pointer" }}>✕</button>
              {TODAS_PROVINCIAS.filter(p => !PROVINCIAS_FAVORITAS.some(f => f.id === p.id)).map(p => {
                const activo = provinciaIdsEqual(form.provincia_id, p.id);
                return (
                  <button key={p.id} type="button"
                    onClick={() => seleccionarProvincia(p.id)}
                    style={{ ...chipStyle(activo), fontSize: 11 }}
                    onMouseEnter={e => onEnter(e, activo)}
                    onMouseLeave={e => onLeave(e, activo)}
                  >
                    {p.nombre}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── POBLACIÓN ──────────────────────────────────────────────────────── */}
      {provNorm && (
        <div id="campo-poblacion" style={{ scrollMarginTop: 120 }}>
          <label style={labelStyle}>
            {tv("Población")}
            {cargandoPob && <span style={{ fontWeight: 400, color: c.textMuted, marginLeft: 8, textTransform: "none" }}>{tv("Cargando...")}</span>}
          </label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>

            {pobFav.map(p => {
              const activo = poblacionIdsEqual(form.poblacion_id, p.id);
              return (
                <button key={p.id} type="button"
                  onClick={() => seleccionarPoblacion(p.id, p.nombre)}
                  style={chipStyle(activo)}
                  onMouseEnter={e => onEnter(e, activo)}
                  onMouseLeave={e => onLeave(e, activo)}
                >
                  {p.nombre}
                </button>
              );
            })}

            {/* Población activa que no es favorita */}
            {pobNorm && !pobActualFav && pobActual && (
              <button type="button" style={chipStyle(true)}>{pobActual.nombre}</button>
            )}

            {/* Botón + Más */}
            {!mostrarMasPoblaciones ? (
              <button type="button"
                onClick={() => setMostrarMasPoblaciones(true)}
                style={{ ...chipStyle(false), color: c.accentBlue, fontWeight: 700 }}>
                + {tv("Más")}
              </button>
            ) : (
              <div style={{ ...panelStyle, paddingTop: 28, maxHeight: 260 }}>
                <button type="button" onClick={() => setMostrarMasPoblaciones(false)}
                  style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: c.textMuted, fontSize: 16, cursor: "pointer" }}>✕</button>
                {pobListaMas.map(p => {
                  const activo = poblacionIdsEqual(form.poblacion_id, p.id);
                  const esFav = pobFavIds.has(normalizePoblacionId(p.id));
                  return (
                    <button key={p.id} type="button"
                      onClick={() => seleccionarPoblacion(p.id, p.nombre)}
                      style={{
                        ...chipStyle(activo),
                        fontSize: 11,
                        fontWeight: esFav || activo ? 700 : 500,
                      }}
                      onMouseEnter={e => onEnter(e, activo)}
                      onMouseLeave={e => onLeave(e, activo)}
                    >
                      {p.nombre}
                    </button>
                  );
                })}
                {pobListaMas.length === 0 && (
                  <p style={{ fontSize: 12, color: c.textMuted, margin: 0 }}>{tv("No hay más poblaciones")}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ZONA, URBANIZACIÓN Y CP ────────────────────────────────────────── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {puedeVerZona && (
        <div style={{ minWidth: 170 }}>
          <label style={labelStyle}>{tv("Zona")}</label>
          <select name="zona_id" value={form.zona_id || ""} onChange={cambiar} disabled={!puedeModificarZona}
            style={{ ...selectStyle, width: "auto", minWidth: 160, opacity: puedeModificarZona ? 1 : 0.6 }}>
            <option value="">{tv("Sin zona")}</option>
            {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
          </select>
        </div>
        )}
        <div style={{ minWidth: 170 }}>
          <label style={labelStyle}>{tv("Urbanización")}</label>
          <select name="urbanizacion_id" value={form.urbanizacion_id || ""} onChange={cambiar}
            style={{ ...selectStyle, width: "auto", minWidth: 160 }}>
            <option value="">{tv("Sin urbanización")}</option>
            {urbanizaciones.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        </div>
        {puedeVerCodigoPostal && (
        <div style={{ width: 120 }}>
          <label style={labelStyle}>{tv("C.P.")}</label>
          <input
            type="text"
            id="campo-codigo_postal"
            name="codigo_postal"
            value={form.codigo_postal || ""}
            onChange={onCodigoPostalChange}
            maxLength={5}
            disabled={!puedeModificarCodigoPostal}
            title={puedeModificarCodigoPostal ? "Editable: se guarda al guardar la ficha" : undefined}
            style={{ ...inputStyle, width: "100%", scrollMarginTop: 120, opacity: puedeModificarCodigoPostal ? 1 : 0.6, background: form.codigo_postal ? (isDark ? "#0f1a10" : "#f0fdf4") : (isDark ? '#0a0e16' : '#dceeff') }}
          />
          {form.codigo_postal && (
            <div style={{ fontSize: 10, color: c.accentGreen, marginTop: 2 }}>
              {esCodigoPostalManual(form) ? tv("✓ Manual") : tv("✓ Auto")}
            </div>
          )}
        </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${c.sectionBorder}` }} />

      {/* ── DIRECCIÓN ─────────────────────────────────────────────────────── */}
      <div ref={direccionRef}>
        <label style={labelStyle}>{tv("Dirección")}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <div style={{ minWidth: 130 }}>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 11, marginBottom: 4 }}>{tv("Tipo vía")}</label>
            <select name="tipo_via_id" value={form.tipo_via_id || ""} onChange={cambiar} onBlur={onDireccionBlur}
              style={{ ...selectStyle, width: "auto", minWidth: 120 }}>
              <option value="">{tv("Tipo")}</option>
              {tiposVia.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          {puedeVerNombreVia && (
          <div style={{ flex: 3, minWidth: 200 }}>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 11, marginBottom: 4 }}>{tv("Nombre de la vía")}</label>
            <AddressAutocompleteInput
              id="campo-nombre_via"
              name="nombre_via"
              value={form.nombre_via || ""}
              onValueChange={(v) => cambiarDirecto("nombre_via", v)}
              onBlur={onDireccionBlur}
              placeholder={tv("Calle, avenida...")}
              style={{ ...inputStyle, scrollMarginTop: 120, opacity: puedeModificarNombreVia ? 1 : 0.6 }}
              disabled={!puedeModificarNombreVia}
              placesLocalityContext={placesMunicipioContext}
              onPlaceAddressResolved={async (parts) => {
                // Nueva sugerencia Places: confiar en CP de Google (el usuario puede volver a fijarlo a mano).
                cambiarDirecto("codigo_postal_manual", false);
                cpManualRef.current = false;
                aplicarCalleDesdePlaces(parts);
                if (parts.codigo_postal) setCodigoPostalAuto(parts.codigo_postal);
                await aplicarLocalidadDesdePlaces(parts);
                aplicarCoordsDesdePlaces(parts);
                if (parts.latitud == null || parts.longitud == null) {
                  setTimeout(() => void ejecutarGeocode(), 200);
                }
              }}
            />
          </div>
          )}
          {[
            { name: "numero", label: "Nº", w: 70, permId: "numero_piso" },
            { name: "bloque", label: "Bloque", w: 80, permId: "numero_piso" },
            { name: "escalera", label: "Escalera", w: 80, permId: "numero_piso" },
            { name: "piso", label: "Piso", w: 70, permId: "numero_piso" },
            { name: "puerta", label: "Puerta", w: 70, permId: "numero_piso" },
          ].map(({ name, label, w, permId }) => {
            if (permId && !puedeVerNumeroPiso) return null;
            const disabledPerm = permId ? !puedeModificarNumeroPiso : false;
            return (
              <div key={name} style={{ width: w }}>
                <label style={{ ...labelStyle, textTransform: "none", fontSize: 11, marginBottom: 4 }}>{tv(label)}</label>
                <input type="text" name={name} value={form[name] || ""} onChange={cambiar} onBlur={onDireccionBlur}
                  disabled={disabledPerm}
                  style={{ ...inputStyle, width: "100%", opacity: disabledPerm ? 0.6 : 1 }} />
              </div>
            );
          })}
        </div>

        {/* Coordenadas + mapa satélite (pin arrastrable, como en portales top) */}
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          {puedeVerGps && (
          <div style={{ flex: "1 1 200px", minWidth: 180, maxWidth: 360 }}>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 11, marginBottom: 4 }}>
              {tv("Coordenadas")}
            </label>
            {isNarrow && puedeVerLocUsarUbicacion && (
              <button
                type="button"
                onClick={usarUbicacionActual}
                disabled={gpsBusy}
                title="Usar la ubicación GPS de este dispositivo y rellenar dirección"
                style={{ ...btnGpsStyle, marginBottom: 8 }}
              >
                {obteniendoGps ? tv("Obteniendo GPS…") : reverseGeocificando ? tv("Actualizando dirección…") : tv("📍 Usar mi ubicación")}
              </button>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                id="campo-coords-gps"
                type="text"
                value={coordsTexto}
                onChange={(e) => setCoordsTexto(e.target.value)}
                onFocus={() => {
                  coordsEditandoRef.current = true;
                }}
                onBlur={onCoordsManualBlur}
                placeholder="38.840840, 0.105690"
                title="Latitud, longitud — al salir actualiza dirección y mapa"
                disabled={!puedeModificarGps}
                style={{
                  ...inputStyle,
                  flex: "1 1 160px",
                  minWidth: isNarrow ? "100%" : 140,
                  minHeight: isNarrow ? 44 : 36,
                  opacity: puedeModificarGps ? 1 : 0.6,
                  color: coordsVerde ? colorCoords : c.textPrimary,
                  fontWeight: coordsVerde ? 600 : 400,
                  borderColor: coordsVerde ? colorCoords : c.chipBorder,
                  background: coordsVerde
                    ? (isDark ? "#0a1f12" : "#f0fdf4")
                    : (isDark ? "#0a0e16" : "#dceeff"),
                }}
              />
              {!isNarrow && puedeVerLocUsarUbicacion && (
                <button
                  type="button"
                  onClick={usarUbicacionActual}
                  disabled={gpsBusy}
                  title="Usar la ubicación GPS de este dispositivo y rellenar dirección"
                  style={btnGpsStyle}
                >
                  {obteniendoGps ? tv("GPS…") : tv("📍 Aquí")}
                </button>
              )}
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 11,
                    color: colorCoords,
                    fontWeight: 600,
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                  }}
                  title="Abrir en Google Maps"
                >
                  Maps ↗
                </a>
              )}
            </div>
            {(geocodificando || reverseGeocificando || obteniendoGps) && (
              <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>
                {obteniendoGps
                  ? tv("Obteniendo ubicación GPS…")
                  : reverseGeocificando
                    ? tv("Actualizando dirección desde coordenadas…")
                    : tv("Obteniendo coordenadas…")}
              </div>
            )}
            {!geocodificando && !reverseGeocificando && !obteniendoGps && !coordsTexto.trim() && (
              <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>
                {tv("Escribe coordenadas o complétalas desde la dirección")}
              </div>
            )}
            {geocodeError && (
              <div style={{ fontSize: 10, color: "#ef4444", marginTop: 4 }}>{geocodeError}</div>
            )}
            {textoCoords && !geocodificando && !reverseGeocificando && !obteniendoGps && !geocodeError && (
              <div style={{ fontSize: 10, color: c.accentGreen, marginTop: 2 }}>
                ✓ {tv("Editable · pin y dirección se sincronizan")}
              </div>
            )}
          </div>
          )}

          {puedeVerLocMapa && (
          <div style={{ flex: "2 1 300px", minWidth: isNarrow ? "100%" : 280, maxWidth: isNarrow ? "100%" : 560 }}>
            <label style={{ ...labelStyle, textTransform: "none", fontSize: 11, marginBottom: 4 }}>
              {tv("Ubicación en mapa")}
            </label>
            <PropiedadMapaUbicacion
              lat={form.latitud}
              lng={form.longitud}
              onCoordsChange={puedeModificarLocMapa ? onMapCoordsChange : undefined}
              fallbackCenter={mapaFallbackCenter}
              active={mapaActivo}
              isDark={isDark}
              borderColor={c.chipBorder}
            />
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
